import { useCallback, useContext, useMemo, useState } from "react";
import {
    Alert,
    Avatar,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { useStyles } from "./MillUser.styles";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { UsersEntity } from "entities/Users/UsersEntity";
import { useParams } from "react-router-dom";
import usePagination from "hooks/usePagination";
import TablePaginator from "components/TablePaginator/TablePaginator.view";
import { useTranslation } from "react-i18next";
import AddOrEditUser from "./AddOrEditUser";
import { IMillUserProps, IUserWithClusterOrMachineCenters } from "./MillUserInterfaces";
import useRolesPermissions from "hooks/useRolesPermissions";
import { DefaultTranslationFieldsInTable, PROTECTED_USER_TYPE_IDS, USERTYPE } from "constants/constants";
import { UserContext } from "contexts/UserContext/UserContext";
import CheckUserPasswordModal from "components/CheckUserPasswordModal";
import { translateDefaultEntity } from "utilities/localesUtilities";

const itemsPerPage = 20;

const MillUser = ({ clusterAllowed }: IMillUserProps) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { millId } = useParams();
    const { user_id } = useContext(UserContext);
    const { currentUser } = UsersEntity.GetCurrentUser(Number(user_id));
    const { deleteUser } = UsersEntity.SoftDeleteUser();
    const [anchorElForActionMenu, setAnchorElForActionMenu] = useState<null | HTMLElement>(null);
    const [openAddUser, setOpenAddUser] = useState(false);
    const [openDeleteUser, setOpenDeleteUser] = useState(false);
    const { users, refetch, loading, totalCount } = UsersEntity.GetPaginatedUsers(Number(millId), itemsPerPage, 0);
    const { currentPage, handleNextPage, handlePreviousPage, totalPages, isFirstPage, isLastPage, handleSetPage } =
        usePagination(totalCount, itemsPerPage, refetch);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [userToEdit, setUserToEdit] = useState<IUserWithClusterOrMachineCenters | null>(null);
    const [userToDelete, setUserToDelete] = useState<IUserWithClusterOrMachineCenters | null>(null);
    const isMenuOpen = Boolean(anchorElForActionMenu);

    const parsedUsers = useMemo(() => users as unknown as IUserWithClusterOrMachineCenters[], [users]);
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();

    const handleClickMoreActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElForActionMenu(event.currentTarget);
    };

    const handleCloseMoreActionsMenu = () => {
        setAnchorElForActionMenu(null);
    };

    const handleOnClickEdit = (user: IUserWithClusterOrMachineCenters) => {
        handleCloseMoreActionsMenu();
        setUserToEdit(user);
    };

    const handleOnClickDelete = (user: IUserWithClusterOrMachineCenters) => {
        setUserToDelete(user);
        setOpenDeleteUser(true);
    };

    const handleUserDeletion = useCallback(async () => {
        try {
            const response = await deleteUser({
                variables: { user_id: userToDelete?.user_id },
            });
            if (response) {
                setSnackbarMessage(t("locations.user_tab.user_deleted"));
                await refetch();
            }
        } catch (error) {
            throw Error(JSON.stringify(error));
        }
    }, [userToDelete, deleteUser, refetch, t]);

    if (loading && !users) {
        return (
            <div className={`${classes.mainCard} ${classes.mainCardLoading}`}>
                <CircularProgress color="info" />
            </div>
        );
    }

    return (
        <div className={classes.mainWrapper}>
            <div className={classes.mainCard}>
                <div className={classes.cardHeader}>
                    <div className={classes.cardHeaderTitle}>{t("locations.user_tab.title")}</div>
                    <div className={classes.cardHeaderActions}>
                        {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditUsers && (
                            <div className={classes.cardHeaderAction} onClick={() => setOpenAddUser(true)}>
                                <AddCircleIcon className={classes.cardHeaderActionIcon} />
                                <span>{t("locations.user_tab.add_user.title")}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={classes.cardContent}>
                    <TableContainer>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.tableHeaderCell} />
                                    <TableCell className={classes.tableHeaderCell}>
                                        {t("locations.user_tab.first_name")}
                                    </TableCell>
                                    <TableCell align="left" className={classes.tableHeaderCell}>
                                        {t("locations.user_tab.last_name")}
                                    </TableCell>
                                    <TableCell align="left" className={classes.tableHeaderCell}>
                                        {t("locations.user_tab.employee_id")}
                                    </TableCell>
                                    <TableCell align="left" className={classes.tableHeaderCell}>
                                        {t("locations.user_tab.email")}
                                    </TableCell>
                                    <TableCell align="left" className={classes.tableHeaderCell}>
                                        {t("locations.user_tab.user_type")}
                                    </TableCell>
                                    {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditUsers && (
                                        <TableCell className={classes.tableHeaderCell} />
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {parsedUsers.map((user) => {
                                    // Corporation-scope user types are editable only by a Corporate
                                    // Admin or an Osorno Admin. Keyed off the same constant that
                                    // gates the AddOrEditUser dropdown so the two cannot drift:
                                    // OS-2557 widened getPaginatedUsers to surface API
                                    // Administrators (mill_id null) in every mill's Users tab, and
                                    // a mill Manager must not be able to repoint one at their mill.
                                    const disableEditAction =
                                        (!CORPORATE_PERMISSIONS.corporationPermissions.canEditUsers ||
                                            (currentUser?.user_type?.user_type_id !== USERTYPE.ADMINISTRATOR &&
                                                currentUser?.user_type?.user_type_id !==
                                                    USERTYPE.OSORNOADMINISTRATOR)) &&
                                        PROTECTED_USER_TYPE_IDS.includes(user.user_type.user_type_id);

                                    const disableDeleteAction = !CORPORATE_PERMISSIONS.corporationPermissions.canDelete;
                                    return (
                                        <TableRow key={user.user_id}>
                                            <TableCell component="th" scope="users" className={classes.tableBodyCell}>
                                                <Avatar className={classes.tableAvatar} src={user.media?.media_url} />
                                            </TableCell>
                                            <TableCell align="left" className={classes.tableBodyCell}>
                                                {user.first_name}
                                            </TableCell>
                                            <TableCell align="left" className={classes.tableBodyCell}>
                                                {user.last_name}
                                            </TableCell>
                                            <TableCell align="left" className={classes.tableBodyCell}>
                                                {user.employee_number ? user.employee_number : "--"}
                                            </TableCell>
                                            <TableCell align="left" className={classes.tableBodyCell}>
                                                <a href={`mailto:${user.email}`}>{user.email || "--"}</a>
                                            </TableCell>
                                            <TableCell align="left" className={classes.tableBodyCell}>
                                                {translateDefaultEntity(
                                                    i18n.language,
                                                    user?.user_type,
                                                    "name",
                                                    DefaultTranslationFieldsInTable.USER_TYPE,
                                                )}
                                            </TableCell>
                                            {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditUsers && (
                                                <TableCell align="right" className={classes.tableBodyCell}>
                                                    <IconButton
                                                        className={classes.tableBodyCellActionButton}
                                                        id={`more-actions-button-${user.user_id}`}
                                                        aria-controls={
                                                            isMenuOpen ? `more-actions-menu-${user.user_id}` : undefined
                                                        }
                                                        aria-haspopup="true"
                                                        aria-expanded={isMenuOpen ? "true" : undefined}
                                                        onClick={handleClickMoreActionsMenu}
                                                    >
                                                        <MoreHorizIcon />
                                                    </IconButton>
                                                    <Menu
                                                        id={`more-actions-menu-${user.user_id}`}
                                                        aria-labelledby="more-actions-button"
                                                        anchorEl={anchorElForActionMenu}
                                                        open={
                                                            isMenuOpen &&
                                                            anchorElForActionMenu?.id ===
                                                                `more-actions-button-${user.user_id}`
                                                        }
                                                        onClose={handleCloseMoreActionsMenu}
                                                        anchorOrigin={{
                                                            vertical: "top",
                                                            horizontal: "left",
                                                        }}
                                                        transformOrigin={{
                                                            vertical: "top",
                                                            horizontal: "left",
                                                        }}
                                                    >
                                                        <MenuItem
                                                            onClick={() => handleOnClickEdit(user)}
                                                            disabled={disableEditAction}
                                                        >
                                                            {t("locations.user_tab.edit")}
                                                        </MenuItem>
                                                        <MenuItem
                                                            onClick={() => handleOnClickDelete(user)}
                                                            disabled={disableDeleteAction}
                                                        >
                                                            {t("locations.user_tab.remove")}
                                                        </MenuItem>
                                                    </Menu>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePaginator
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onClickNextPage={handleNextPage}
                        onClickPreviousPage={handlePreviousPage}
                        isFirstPage={isFirstPage}
                        isLastPage={isLastPage}
                    />
                    <AddOrEditUser
                        open={openAddUser || userToEdit !== null}
                        user={userToEdit}
                        onAddUser={() => {
                            refetch();
                            handleSetPage(1);
                            setOpenAddUser(false);
                            setSnackbarMessage(t("locations.user_tab.user_added"));
                        }}
                        onEditUser={() => {
                            refetch();
                            handleSetPage(1);
                            setOpenAddUser(false);
                            setUserToEdit(null);
                            setSnackbarMessage(t("locations.user_tab.user_edited"));
                        }}
                        onClose={() => {
                            setOpenAddUser(false);
                            setUserToEdit(null);
                        }}
                        clusterAllowed={clusterAllowed}
                    />
                    <CheckUserPasswordModal
                        open={openDeleteUser}
                        onConfirmPassword={handleUserDeletion}
                        onClose={() => setOpenDeleteUser(false)}
                        title={t("locations.user_tab.delete_user.header")}
                        question={t("locations.user_tab.delete_user.confirm_modal_question", {
                            name: `${userToDelete?.first_name} ${userToDelete?.last_name}`,
                        })}
                        questionConfirmedButtonText={t("locations.user_tab.delete_user.header")}
                    />
                    <Snackbar
                        open={snackbarMessage !== ""}
                        onClose={() => setSnackbarMessage("")}
                        autoHideDuration={6000}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Alert severity="success" sx={{ width: "100%" }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
};

export default MillUser;
